// Portfolio dashboard tokens — derived from REPORT (Blue Ridge Hazier) palette.
// Centralised here so all portfolio components stay visually coherent without
// re-deriving the same tint maps in five files.

import { REPORT } from '../print/PrintAtoms';
import type { Tone } from '../../lib/portfolioApi';

export const PORTFOLIO_TOKENS = {
  serif: 'Georgia, "Iowan Old Style", "Times New Roman", serif',
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  ruleSoft: 'rgba(46,56,64,0.06)',
  ruleMid: 'rgba(46,56,64,0.14)',
  ruleHard: 'rgba(46,56,64,0.22)',
  amberWash: REPORT.amberWash,
  sand: REPORT.sand,
  // patina = sunrise-light, the warm coral used by Briefing v1's dark-band
  // header for eyebrow + brand text on REPORT.heading backgrounds.
  patina: REPORT.patina,
} as const;

// Tinted backgrounds + colors for grade cells (matches v3 mockup + REPORT pattern).
export interface ToneStyle {
  bg: string;
  border: string;
  fg: string;
  bgWash: string;
}

export function toneStyle(tone: Tone): ToneStyle {
  switch (tone) {
    case 'pass':
      return {
        bg: 'rgba(92,107,69,0.07)',
        border: 'rgba(92,107,69,0.22)',
        fg: REPORT.passText,
        bgWash: REPORT.passWash,
      };
    case 'caution':
      return {
        bg: 'rgba(154,123,48,0.07)',
        border: 'rgba(154,123,48,0.22)',
        fg: REPORT.cautionText,
        bgWash: REPORT.cautionWash,
      };
    case 'fail':
      return {
        bg: 'rgba(160,64,48,0.07)',
        border: 'rgba(160,64,48,0.22)',
        fg: REPORT.failText,
        bgWash: REPORT.failWash,
      };
    default:
      return {
        bg: REPORT.white,
        border: PORTFOLIO_TOKENS.ruleMid,
        fg: REPORT.heading,
        bgWash: REPORT.surface,
      };
  }
}

// Map a letter grade prefix to a tone, used by the right-rail GradeCard.
export function gradeTone(grade: string | null): Tone {
  if (!grade) return 'neutral';
  const first = grade[0]?.toUpperCase();
  if (first === 'A') return 'pass';
  if (first === 'B') return 'caution';
  return 'fail';
}

// Split a grade string ("A+", "B-", "A") into letter + sign for the GradeCard layout.
export function splitGrade(grade: string | null): { letter: string; sign: string | null } {
  if (!grade) return { letter: '—', sign: null };
  const m = grade.match(/^([A-D])([+\-])?$/i);
  if (!m) return { letter: grade, sign: null };
  return { letter: m[1].toUpperCase(), sign: m[2] || null };
}
